import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import "./index.css";

gsap.registerPlugin(ScrollTrigger);

export default function DocksComingSoon() {
  const containerRef = useRef(null);
  const symbolRef = useRef(null);
  const docksContainerRef = useRef(null);
  const docksLettersRef = useRef([]);
  const dockingSoonRef = useRef(null);
  const taglineRef = useRef(null);
  const particlesRef = useRef([]);
  const scrollIndicatorRef = useRef(null);

  const docksText = "døcks";

// Generate particle data
const particles = React.useMemo(() => {
  const particleCount = window.innerWidth < 768 ? 8 : 15;
  return Array.from({ length: particleCount }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: window.innerWidth < 768 ? 4 + Math.random() * 6 : 5 + Math.random() * 10,
    speed: 5 + Math.random() * 5,
    delay: Math.random() * 5,
  }));
}, []);

  useEffect(() => {
    // Timeline for ø → døcks → fade out
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top top",
        end: "+=1500",
        scrub: true,
      },
    });

    // ø fades out and scales down (stays in same position)
    tl.to(symbolRef.current, { 
      opacity: 0, 
      scale: 0.3, 
      duration: 1
    });

    // døcks container fades in at the same time
    tl.fromTo(
      docksContainerRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 1 },
      0 // Start at the same time as symbol fade out
    );

    // døcks letters fade in sequentially
    docksLettersRef.current.forEach((letter, i) => {
      tl.fromTo(
        letter, 
        { opacity: 0, y: 30 }, 
        { opacity: 1, y: 0, duration: 0.3 }, 
        0.3 + i * 0.1
      );
    });

    // døcks fades out after appearing
    tl.to(docksContainerRef.current, {
      opacity: 0,
      scale: 0.8,
      duration: 1,
    }, "+=0.5");

    // døcking soon fade up
    gsap.fromTo(
      dockingSoonRef.current,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top+=1200 top",
          end: "+=400",
          scrub: true,
        },
      }
    );

    // tagline fade up
    gsap.fromTo(
      taglineRef.current,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top+=1600 top",
          end: "+=400",
          scrub: true,
        },
      }
    );

    // Scroll indicator fades out when scrolling starts
    gsap.to(scrollIndicatorRef.current, {
      opacity: 0,
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top top",
        end: "+=200",
        scrub: true,
      },
    });

// Animate floating particles
    particlesRef.current.forEach((p, i) => {
      gsap.to(p, {
        y: `+=${window.innerHeight + Math.random() * 300}`, // Move down instead of up
        x: `+=${Math.random() * 60 - 30}`, // More horizontal movement
        rotation: Math.random() * 360,
        duration: particles[i].speed,
        repeat: -1,
        ease: "sine.inOut",
        delay: particles[i].delay,
      });
    });
  }, [particles]);


  return (
    <div ref={containerRef} className="scroll-container">
      <div style={{ position: "relative", minHeight: "300vh" }}>
        {/* Sticky Hero */}
        <div
          style={{
            position: "sticky",
            top: 0,
            height: "100vh",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            overflow: "hidden",
            backgroundColor: "black",
          }}
         >
          {/* Floating Particles */}
          {particles.map((p, i) => (
            <div
              key={p.id}
              className="particle"
              ref={(el) => (particlesRef.current[i] = el)}
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                fontSize: `${p.size}px`,
              }}
            >
              ø
            </div>
          ))}
          {/* Content Wrapper - everything centered in same position */}
          <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}>
            {/* Splash ø */}
            <div
              ref={symbolRef}
              style={{
                fontSize: "min(25vw, 400px)",
                color: "white",
                fontWeight: "bold",
                position: "absolute",
              }}
            >
              ø
            </div>

            {/* døcks letters - positioned in same spot */}
            <div
              ref={docksContainerRef}
              style={{
                display: "flex",
                gap: "0",
                letterSpacing: "-0.08em",
                fontSize: "min(15vw, 240px)",
                color: "white",
                fontWeight: "bold",
                position: "absolute",
                opacity: 0,
              }}
            >
              {docksText.split("").map((letter, i) => (
                <span
                  key={i}
                  className="docks-letter"
                  ref={(el) => (docksLettersRef.current[i] = el)}
                >
                  {letter}
                </span>
              ))}
            </div>

            {/* døcking soon */}
            <div
              ref={dockingSoonRef}
              style={{
                display: "flex",
                fontSize: "min(7vw, 112px)",
                letterSpacing: "-0.08em",
                color: "white",
                fontWeight: "bold",
                position: "absolute",
                top: "50%",
                opacity: 0,
                whiteSpace: "nowrap",
              }}
            >
              døcking soon
            </div>
          </div>

          {/* Tagline */}
          <div
            ref={taglineRef}
            style={{
              fontSize: "min(4vw, 32px)",
              color: "white",
              letterSpacing: "-0.08em",
              fontStyle: "italic",
              position: "absolute",
              fontWeight: 'normal',
              bottom: "10%",
              opacity: 0,
              textAlign: "center",
              padding: "0 2rem",
            }}
          >
            weaving something wild
          </div>

          {/* Scroll Indicator */}
          <div ref={scrollIndicatorRef} className="scroll-indicator">
            <div>scroll</div>
            <div>↓</div>
          </div>
        </div>

        {/* Extra spacer for scroll */}
        <div style={{ height: "200vh" }}></div>
      </div>
    </div>
  );
}